import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Users, Plus, ArrowRight } from 'lucide-react';

const AddMembersModal = ({ open, onClose, onPurchase, currentLimit, isYearly = false }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Pricing: ₹249/month per member for monthly, ₹2,490/year per member for yearly
  const pricePerMember = isYearly ? 2490 : 249;
  const billingCycle = isYearly ? 'year' : 'month';

  const memberPackages = [
    { id: 1, count: 1, price: pricePerMember * 1, label: 'Add 1 Member', popular: false },
    { id: 2, count: 2, price: pricePerMember * 2, label: 'Add 2 Members', popular: true },
    { id: 5, count: 5, price: pricePerMember * 5, label: 'Add 5 Members', popular: false },
    { id: 10, count: 10, price: pricePerMember * 10, label: 'Add 10 Members', popular: false }
  ];

  const handlePurchase = () => {
    if (selectedPackage) {
      const pkg = memberPackages.find(p => p.id === selectedPackage);
      onPurchase(pkg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-600" />
            Add More Team Members
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Status */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Current Limit:</span> {currentLimit} members
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Add additional members at ₹{pricePerMember.toLocaleString()}/{billingCycle} per member
            </p>
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {memberPackages.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative cursor-pointer border-2 rounded-lg p-4 transition-all ${
                  selectedPackage === pkg.id
                    ? 'border-amber-600 bg-amber-50 shadow-md'
                    : 'border-gray-200 hover:border-amber-300 hover:shadow-sm'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 right-4 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                    Popular
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{pkg.label}</p>
                      <p className="text-xs text-gray-500">
                        New limit: {currentLimit + pkg.count}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">₹{pkg.price.toLocaleString()}</span>
                      <span className="text-sm text-gray-500">/{billingCycle}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      ₹{pricePerMember.toLocaleString()}/member
                    </div>
                  </div>
                </div>

                {selectedPackage === pkg.id && (
                  <div className="absolute inset-0 border-2 border-amber-600 rounded-lg pointer-events-none">
                    <div className="absolute top-2 right-2 w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-900 mb-2">What's Included:</p>
            <ul className="space-y-1 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Full access to all CRM features
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Unlimited pipelines and deals
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Team collaboration tools
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Cancel anytime
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={!selectedPackage}
              className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
            >
              Continue to Payment
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembersModal;
